/**
 * Shell layout for the admin space.
 *
 * Same structure as PortalLayout (fixed sidebar + mobile drawer + outlet),
 * but mounts AdminSidebar instead and uses /admin scoped routes.
 */
import { useState, useCallback, useEffect } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import AdminSidebar from "./AdminSidebar";

const LOGO_MARK = "/logomark.png";

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const onLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 px-4 h-14 flex items-center justify-between">
        <Link
          to="/"
          data-testid="admin-mobile-logo-home"
          className="flex items-center gap-2"
        >
          <img src={LOGO_MARK} alt="Adonis Agency" className="h-8 w-8" />
          <span className="font-heading font-bold text-sm tracking-tight text-slate-900">
            Adonis Agency
          </span>
        </Link>
        <button
          type="button"
          data-testid="admin-mobile-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="p-2 -mr-2 text-slate-900"
          aria-label="Menu"
        >
          {mobileOpen ? <X size={22} /> : <List size={22} />}
        </button>
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 z-30 w-64 bg-white border-r border-slate-200 px-4 pt-6 pb-6 flex-col overflow-hidden">
        <AdminSidebar user={user} onLogout={onLogout} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <aside
            data-testid="admin-mobile-drawer"
            className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-slate-200 px-4 pt-6 pb-6 overflow-hidden flex flex-col"
          >
            <AdminSidebar
              user={user}
              onLogout={onLogout}
              onItemClick={() => setMobileOpen(false)}
            />
          </aside>
        </>
      )}

      {/* Main */}
      <main className="lg:pl-64">
        <div className="px-6 md:px-10 lg:px-14 py-8 md:py-12 w-full max-w-[1600px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
